import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc, updateDoc } from 'firebase/firestore';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { UserProfile, Lesson, Relationship, Transaction, Review } from '../types';
import { 
  TrendingUp, 
  Loader2, 
  CheckCircle2,
  Zap,
  ArrowLeft,
  Activity,
  Filter,
  ChevronDown,
  DollarSign,
  Target,
  Users,
  Calendar,
  AlertCircle,
  Award,
  TrendingDown,
  Settings2,
  X,
  Save,
  Check,
  CreditCard,
  BarChart3,
  ChevronLeft
} from 'lucide-react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { format, subMonths, subWeeks, startOfMonth, endOfMonth, eachMonthOfInterval, startOfYear, getYear, getMonth } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { useTranslation } from '../lib/translations';

interface StatsProps {
  profile: UserProfile | null;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  'USD': '$', 'EUR': '€', 'GBP': '£', 'CAD': 'C$', 'AUD': 'A$', 'JPY': '¥', 'CHF': 'Fr.', 'BRL': 'R$',
};

const GoalProgressBar: React.FC<{ value: number; label: string; subLabel: string; color: string }> = ({ value, label, subLabel, color }) => {
  const percentage = Math.min(100, Math.max(0, value));

  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between">
        <div>
          <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-900 dark:text-white leading-none">{label}</h4>
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter mt-1">{subLabel}</p>
        </div>
        <span className="text-sm font-black text-slate-900 dark:text-white">{Math.round(percentage)}%</span>
      </div>
      <div className="h-2.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden shadow-inner">
        <div 
          className="h-full rounded-full transition-all duration-1000 ease-out shadow-sm"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const Stats: React.FC<StatsProps> = ({ profile }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [avgRating, setAvgRating] = useState<string | null>(null);
  
  const [drilldownYear, setDrilldownYear] = useState<number | null>(null);
  const [revDrilldownYear, setRevDrilldownYear] = useState<number | null>(null);
  
  const [isGoalModalOpen, setIsGoalModalOpen] = useState(false);
  const [goalRoster, setGoalRoster] = useState(profile?.goalRosterSize || 10);
  const [goalVolume, setGoalVolume] = useState(profile?.goalWeeklyVolume || 10);
  const [savingGoals, setSavingGoals] = useState(false);

  const { t, language } = useTranslation();
  const locale = language === 'fr' ? fr : enUS;
  const selectedStudentId = searchParams.get('studentId') || '';
  const currencySymbol = profile?.currency ? (CURRENCY_SYMBOLS[profile.currency] || '€') : '€';

  useEffect(() => { 
    setDrilldownYear(null); 
    setRevDrilldownYear(null);
  }, [selectedStudentId]);

  const fetchData = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const relSnap = await getDocs(query(collection(db, 'relationships'), where('teacherId', '==', profile.uid)));
      const rels = await Promise.all(relSnap.docs.map(async d => {
        const docData = d.data() as Relationship;
        const studentId = docData.studentId;
        const isPending = studentId.startsWith('pending:');
        let name = '';
        if (isPending) { 
          name = studentId.split(':')[1].split('@')[0]; 
        } else { 
          const uDoc = await getDoc(doc(db, 'users', studentId)); 
          name = uDoc.data()?.displayName || 'Unknown'; 
        }
        return { ...docData, id: d.id, studentName: name } as Relationship;
      }));
      setRelationships(rels);

      let transQ = query(collection(db, 'transactions'), where('teacherId', '==', profile.uid));
      if (selectedStudentId) { 
        transQ = query(collection(db, 'transactions'), where('teacherId', '==', profile.uid), where('studentId', '==', selectedStudentId)); 
      }
      const transSnap = await getDocs(transQ);
      setTransactions(transSnap.docs.map(d => ({ id: d.id, ...d.data() } as Transaction)));

      let lessonsQ;
      if (selectedStudentId) { 
        lessonsQ = query(collection(db, 'lessons'), where('teacherId', '==', profile.uid), where('studentId', '==', selectedStudentId), orderBy('start', 'asc')); 
      } else { 
        lessonsQ = query(collection(db, 'lessons'), where('teacherId', '==', profile.uid), orderBy('start', 'asc')); 
      }
      const lessonSnap = await getDocs(lessonsQ);
      setLessons(lessonSnap.docs.map(d => ({ id: d.id, ...d.data() } as Lesson)));

      let reviewsQ = query(collection(db, 'reviews'), where('teacherId', '==', profile.uid));
      if (selectedStudentId) { 
        reviewsQ = query(collection(db, 'reviews'), where('teacherId', '==', profile.uid), where('studentId', '==', selectedStudentId)); 
      }
      const revSnap = await getDocs(reviewsQ);
      const revs = revSnap.docs.map(d => d.data() as Review);
      setReviewsCount(revs.length);
      if (revs.length > 0) {
        setAvgRating((revs.reduce((acc, r) => acc + r.rating, 0) / revs.length).toFixed(1));
      } else {
        setAvgRating(null);
      }

    } catch (e) { console.error("Failed to fetch stats data:", e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [profile, selectedStudentId]);

  const handleSaveGoals = async () => {
    if (!profile) return;
    setSavingGoals(true);
    try {
      await updateDoc(doc(db, 'users', profile.uid), {
        goalRosterSize: goalRoster,
        goalWeeklyVolume: goalVolume
      });
      setIsGoalModalOpen(false);
    } catch (e) { console.error(e); }
    setSavingGoals(false);
  };

  const stats = useMemo(() => {
    const activeRels = relationships.filter(r => r.status === 'active');
    const activeStudents = activeRels.length;
    const currentRel = selectedStudentId ? relationships.find(r => r.studentId === selectedStudentId) : null;
    const completedLessons = lessons.filter(l => l.status === 'completed');
    const canceledLessons = lessons.filter(l => l.status === 'canceled');
    
    const totalCreditsPaid = selectedStudentId && currentRel ? currentRel.lessonsPaid : relationships.reduce((acc, r) => acc + (r.lessonsPaid || 0), 0);
    const totalCreditsUsed = selectedStudentId && currentRel ? currentRel.lessonsCompleted : relationships.reduce((acc, r) => acc + (r.lessonsCompleted || 0), 0);
    const creditsRemaining = totalCreditsPaid - totalCreditsUsed;

    const hasLessonHistory = (completedLessons.length + canceledLessons.length) > 0;
    const completionRate = hasLessonHistory ? Math.round((completedLessons.length / (completedLessons.length + canceledLessons.length)) * 100) : null;
    
    // Weekly Velocity (12-week average)
    const twelveWeeksAgo = subWeeks(new Date(), 12);
    const weeklyLessons = completedLessons.filter(l => l.start.toDate() > twelveWeeksAgo);
    const weeklyVelocity = (weeklyLessons.length / 12);
    
    // Period-based Revenue Calculation
    const periodRevenue = transactions.reduce((acc, t) => {
      const date = t.date.toDate();
      if (revDrilldownYear !== null) {
        if (getYear(date) !== revDrilldownYear) return acc;
      }
      return acc + t.price;
    }, 0);

    const totalRevenue = transactions.reduce((acc, t) => acc + t.price, 0);

    return { 
      activeStudents, 
      completedLessons, 
      canceledLessons, 
      totalCreditsPaid, 
      totalCreditsUsed, 
      creditsRemaining,
      completionRate, 
      weeklyVelocity, 
      currentRel, 
      totalRevenue, 
      periodRevenue,
      hasLessonHistory,
      rosterProgress: (activeStudents / goalRoster) * 100,
      volumeProgress: (weeklyVelocity / goalVolume) * 100
    };
  }, [lessons, relationships, selectedStudentId, transactions, goalRoster, goalVolume, revDrilldownYear]);

  const lessonCountChartOptions = useMemo(() => {
    const activeColor = selectedStudentId && stats.currentRel?.color ? stats.currentRel.color : '#9333ea';
    const seriesName = selectedStudentId ? stats.currentRel?.studentName : t.common.lessons;
    if (drilldownYear === null) {
      const yearsMap: Record<number, number> = {};
      stats.completedLessons.forEach(l => { 
        const year = l.start.toDate().getFullYear(); 
        if (!selectedStudentId || l.studentId === selectedStudentId) { 
          yearsMap[year] = (yearsMap[year] || 0) + 1; 
        } 
      });
      const categories = Object.keys(yearsMap).sort();
      const data = categories.map(y => yearsMap[parseInt(y)]);
      return { 
        chart: { type: 'column', backgroundColor: 'transparent', height: 300, spacingTop: 20 }, 
        title: { text: null }, 
        xAxis: { categories, labels: { style: { color: '#94a3b8', fontWeight: 'black', fontSize: '11px' } }, lineColor: '#e2e8f0', tickWidth: 0 }, 
        yAxis: { title: { text: null }, gridLineColor: 'rgba(226, 232, 240, 0.5)', labels: { style: { color: '#94a3b8' } }, min: 0, allowDecimals: false }, 
        legend: { enabled: false }, 
        credits: { enabled: false }, 
        plotOptions: { column: { borderRadius: 6, borderWidth: 0, color: activeColor, pointPadding: 0.2, groupPadding: 0.1, states: { hover: { brightness: 0.1 } } } }, 
        series: [{ name: seriesName, data: data.map((val, i) => ({ y: val, cursor: 'pointer', events: { click: () => setDrilldownYear(parseInt(categories[i])) } })) }] 
      };
    } else {
      const months = Array.from({ length: 12 }, (_, i) => i);
      const data = months.map(m => { 
        return stats.completedLessons.filter(l => { 
          const d = l.start.toDate(); 
          return d.getFullYear() === drilldownYear && d.getMonth() === m && (selectedStudentId === l.studentId || !selectedStudentId); 
        }).length; 
      });
      const categories = months.map(m => format(new Date(drilldownYear, m), 'MMM', { locale }));
      return { 
        chart: { type: 'column', backgroundColor: 'transparent', height: 300, spacingTop: 20 }, 
        title: { text: null }, 
        xAxis: { categories, labels: { style: { color: '#94a3b8', fontWeight: 'black', fontSize: '11px' } }, lineColor: '#e2e8f0', tickWidth: 0 }, 
        yAxis: { title: { text: null }, gridLineColor: 'rgba(226, 232, 240, 0.5)', labels: { style: { color: '#94a3b8' } }, min: 0, allowDecimals: false }, 
        legend: { enabled: false }, 
        credits: { enabled: false }, 
        plotOptions: { column: { borderRadius: 6, borderWidth: 0, color: activeColor === '#9333ea' ? '#4f46e5' : activeColor, pointPadding: 0.2, groupPadding: 0.1 } }, 
        series: [{ name: `${seriesName} (${drilldownYear})`, data }] 
      };
    }
  }, [stats.completedLessons, drilldownYear, t, locale, selectedStudentId, stats.currentRel]);

  const revenueChartOptions = useMemo(() => {
    const seriesName = language === 'fr' ? 'Revenus' : 'Revenue';
    const color = '#f59e0b'; // Amber

    if (revDrilldownYear === null) {
      const yearsMap: Record<number, number> = {};
      transactions.forEach(tr => {
        const year = tr.date.toDate().getFullYear();
        yearsMap[year] = (yearsMap[year] || 0) + tr.price;
      });
      const categories = Object.keys(yearsMap).sort();
      const data = categories.map(y => yearsMap[parseInt(y)]);
      return {
        chart: { type: 'areaspline', backgroundColor: 'transparent', height: 180, margin: [10, 10, 30, 40] },
        title: { text: null },
        xAxis: { categories, labels: { style: { color: '#94a3b8', fontWeight: 'black', fontSize: '10px' } }, lineColor: '#e2e8f0', tickWidth: 0 },
        yAxis: { title: { text: null }, gridLineColor: 'rgba(226, 232, 240, 0.3)', labels: { style: { color: '#94a3b8', fontSize: '9px' } }, min: 0 },
        legend: { enabled: false },
        credits: { enabled: false },
        tooltip: { valuePrefix: currencySymbol },
        plotOptions: { areaspline: { color, fillOpacity: 0.1, lineWidth: 3, marker: { enabled: true, radius: 4, fillColor: '#fff', lineColor: color, lineWidth: 2 } } },
        series: [{ name: seriesName, data: data.map((val, i) => ({ y: val, cursor: 'pointer', events: { click: () => setRevDrilldownYear(parseInt(categories[i])) } })) }]
      };
    } else {
      const months = Array.from({ length: 12 }, (_, i) => i);
      const data = months.map(m => {
        return transactions.filter(tr => {
          const d = tr.date.toDate();
          return d.getFullYear() === revDrilldownYear && d.getMonth() === m;
        }).reduce((acc, tr) => acc + tr.price, 0);
      });
      const categories = months.map(m => format(new Date(revDrilldownYear, m), 'MMM', { locale }));
      return {
        chart: { type: 'areaspline', backgroundColor: 'transparent', height: 180, margin: [10, 10, 30, 40] },
        title: { text: null },
        xAxis: { categories, labels: { style: { color: '#94a3b8', fontWeight: 'black', fontSize: '10px' } }, lineColor: '#e2e8f0', tickWidth: 0 },
        yAxis: { title: { text: null }, gridLineColor: 'rgba(226, 232, 240, 0.3)', labels: { style: { color: '#94a3b8', fontSize: '9px' } }, min: 0 },
        legend: { enabled: false },
        credits: { enabled: false },
        tooltip: { valuePrefix: currencySymbol },
        plotOptions: { areaspline: { color, fillOpacity: 0.1, lineWidth: 3, marker: { enabled: true, radius: 4, fillColor: '#fff', lineColor: color, lineWidth: 2 } } },
        series: [{ name: `${seriesName} (${revDrilldownYear})`, data }]
      };
    }
  }, [transactions, revDrilldownYear, language, currencySymbol, locale]);

  const inventoryChartOptions = useMemo(() => {
    return { 
      chart: { type: 'pie', backgroundColor: 'transparent', height: 240 }, 
      title: { text: null }, 
      tooltip: { pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>' }, 
      plotOptions: { pie: { allowPointSelect: true, cursor: 'pointer', dataLabels: { enabled: false }, showInLegend: true, innerSize: '70%', borderWidth: 0 } }, 
      credits: { enabled: false }, 
      legend: { itemStyle: { color: '#94a3b8', fontWeight: 'bold', fontSize: '10px' }, verticalAlign: 'bottom', layout: 'horizontal' }, 
      series: [{ 
        name: t.common.credits, 
        colorByPoint: true, 
        data: [
          { name: t.stats.utilized, y: stats.totalCreditsUsed, color: '#9333ea' }, 
          { name: t.stats.remaining, y: Math.max(0, stats.creditsRemaining), color: '#14b8a6' }
        ] 
      }] 
    };
  }, [stats.totalCreditsPaid, stats.totalCreditsUsed, stats.creditsRemaining, t]);

  if (loading) return (
    <div className="flex flex-col justify-center items-center h-[60vh] gap-4">
      <Loader2 className="animate-spin text-purple-600" size={40} />
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t.common.loading}</p>
    </div>
  );

  return (
    <div className="space-y-10 max-w-7xl mx-auto pb-32 px-4 md:px-0">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-500/20 text-teal-600 dark:text-teal-400 text-[10px] font-black uppercase tracking-widest shadow-sm mb-4">
            <TrendingUp size={12} className="animate-pulse" /> {t.nav.insights}
          </div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">
            {selectedStudentId && stats.currentRel ? t.stats.viewingStatsFor + ' ' + stats.currentRel.studentName : t.stats.title}
          </h1>
          <p className="text-slate-500 font-medium">{t.stats.desc}</p>
        </div>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setIsGoalModalOpen(true)}
            className="flex items-center gap-2 px-6 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl font-black text-[10px] uppercase tracking-widest hover:border-purple-500/30 transition-all shadow-sm"
          >
            <Settings2 size={16} className="text-purple-600" /> {t.stats.setGoals}
          </button>
          <div className="relative group">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <select 
              value={selectedStudentId} 
              onChange={(e) => setSearchParams(e.target.value ? { studentId: e.target.value } : {})}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-lg py-2 pl-9 pr-4 text-xs font-bold outline-none focus:ring-4 focus:ring-purple-500/10 transition-all appearance-none cursor-pointer min-w-[200px]"
            >
              <option value="">{t.stats.allStudents}</option>
              {relationships.map(r => <option key={r.studentId} value={r.studentId}>{r.studentName}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Compact Goal Progression Section */}
        <section className="lg:col-span-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-8 rounded-3xl shadow-sm relative group overflow-hidden h-full">
          <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity">
            <Target size={80} />
          </div>
          <div className="relative z-10 flex flex-col h-full">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 rounded-xl flex items-center justify-center">
                <Target size={20} />
              </div>
              <div>
                <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">{t.stats.goalsTitle}</h3>
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{t.stats.progression}</p>
              </div>
            </div>
            
            <div className="space-y-8 mt-4">
              <GoalProgressBar 
                value={stats.rosterProgress} 
                label={t.stats.activeRoster} 
                subLabel={`${stats.activeStudents} / ${goalRoster} ${t.common.student.toLowerCase()}s`} 
                color="#9333ea" 
              />
              <GoalProgressBar 
                value={stats.volumeProgress} 
                label={t.stats.weeklyVelocity} 
                subLabel={`${stats.weeklyVelocity.toFixed(1)} / ${goalVolume} ${t.common.lessons.toLowerCase()}`} 
                color="#0ea5e9" 
              />
            </div>
            
            <div className="mt-auto pt-10">
              <button 
                onClick={() => setIsGoalModalOpen(true)}
                className="w-full py-3 bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-white/5 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-purple-600 transition-all"
              >
                {t.stats.setGoals}
              </button>
            </div>
          </div>
        </section>

        {/* Global Stats Matrix */}
        <div className="lg:col-span-7 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-6 rounded-2xl shadow-sm">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{t.stats.completedSessions}</p>
              <div className="flex items-end justify-between">
                <span className="text-4xl font-black text-slate-900 dark:text-white">{stats.completedLessons.length}</span>
                <div className="p-2 bg-purple-50 dark:bg-purple-900/20 text-purple-600 rounded-lg"><CheckCircle2 size={20} /></div>
              </div>
            </div>
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-6 rounded-2xl shadow-sm">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">{t.stats.attendanceRate}</p>
              <div className="flex items-end justify-between">
                <span className="text-4xl font-black text-slate-900 dark:text-white">{stats.completionRate || 0}%</span>
                <div className="p-2 bg-teal-50 dark:bg-teal-900/20 text-teal-600 rounded-lg"><Activity size={20} /></div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">
              <DollarSign size={12} className="text-amber-600" /> {language === 'fr' ? 'PERFORMANCE FINANCIÈRE' : 'FINANCIAL PERFORMANCE'}
            </div>
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-8 rounded-3xl shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <DollarSign size={100} />
              </div>
              <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-1 w-full">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">{t.stats.estimatedEarnings}</p>
                    {revDrilldownYear && (
                      <button 
                        onClick={() => setRevDrilldownYear(null)}
                        className="flex items-center gap-1 text-[9px] font-black uppercase text-purple-600 hover:bg-purple-50 px-2 py-1 rounded transition-all"
                      >
                        <ChevronLeft size={12} /> {t.common.back}
                      </button>
                    )}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter">{currencySymbol}{stats.periodRevenue}</span>
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                      {revDrilldownYear ? `${language === 'fr' ? 'EN' : 'IN'} ${revDrilldownYear}` : t.stats.allTime}
                    </span>
                  </div>
                  <div className="mt-8 grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-100 dark:border-white/5">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{language === 'fr' ? 'Forfaits vendus' : 'Packs Sold'}</p>
                        <p className="text-xl font-black text-slate-900 dark:text-white">{transactions.length}</p>
                    </div>
                    <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-100 dark:border-white/5">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{language === 'fr' ? 'Panier moyen' : 'Avg. Transaction'}</p>
                        <p className="text-xl font-black text-slate-900 dark:text-white">{currencySymbol}{transactions.length > 0 ? (stats.totalRevenue / transactions.length).toFixed(0) : 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 w-full">
                   <HighchartsReact highcharts={Highcharts} options={revenueChartOptions} />
                   <p className="text-[9px] text-slate-400 font-bold uppercase text-center mt-2 tracking-widest">
                     {revDrilldownYear ? t.stats.showingMonthly : t.stats.clickYearTip}
                   </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-8 rounded-3xl shadow-sm">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white">{t.stats.volumeTitle}</h3>
              <p className="text-xs text-slate-500 font-medium">{drilldownYear ? t.stats.showingMonthly : t.stats.clickYearTip}</p>
            </div>
            {drilldownYear && (
              <button onClick={() => setDrilldownYear(null)} className="flex items-center gap-2 text-[10px] font-black uppercase text-purple-600 hover:bg-purple-50 p-2 rounded-lg transition-all">
                <ArrowLeft size={14} /> {t.common.back}
              </button>
            )}
          </div>
          <HighchartsReact highcharts={Highcharts} options={lessonCountChartOptions} />
        </div>
        <div className="lg:col-span-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-8 rounded-3xl shadow-sm">
          <h3 className="text-xl font-black text-slate-900 dark:text-white mb-8">{t.stats.lifecycleTitle}</h3>
          <HighchartsReact highcharts={Highcharts} options={inventoryChartOptions} />
          <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-white/5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.stats.creditsAvailable}</span>
              <span className="text-sm font-black text-slate-900 dark:text-white">{stats.creditsRemaining}</span>
            </div>
            <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-600" 
                style={{ width: `${stats.totalCreditsPaid > 0 ? (stats.totalCreditsUsed / stats.totalCreditsPaid) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Goal Modal */}
      {isGoalModalOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300 !mt-0">
          <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl border border-slate-200 dark:border-white/5 p-10 shadow-2xl relative overflow-hidden">
            <button onClick={() => setIsGoalModalOpen(false)} className="absolute top-8 right-8 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all"><X size={24} /></button>
            
            <div className="mb-8">
              <div className="w-14 h-14 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mb-6">
                <Target size={28} className="text-indigo-600" />
              </div>
              <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter mb-2">{t.stats.goalsTitle}</h2>
              <p className="text-slate-500 text-sm font-medium leading-relaxed">Adjust your targets for professional studio orchestration.</p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.stats.targetRoster}</label>
                <div className="relative group">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={18} />
                  <input 
                    type="number" 
                    value={goalRoster} 
                    onChange={(e) => setGoalRoster(parseInt(e.target.value) || 0)} 
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 pl-12 pr-4 text-sm font-black focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all shadow-sm" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">{t.stats.targetVolume}</label>
                <div className="relative group">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={18} />
                  <input 
                    type="number" 
                    value={goalVolume} 
                    onChange={(e) => setGoalVolume(parseInt(e.target.value) || 0)} 
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg py-3 pl-12 pr-4 text-sm font-black focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all shadow-sm" 
                  />
                </div>
              </div>

              <button 
                onClick={handleSaveGoals} 
                disabled={savingGoals} 
                className="w-full py-4 bg-indigo-600 text-white rounded-lg font-black text-[11px] uppercase tracking-[0.2em] transition-all shadow-xl shadow-indigo-500/20 hover:bg-indigo-700 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {savingGoals ? <Loader2 className="animate-spin" size={20} /> : <><Save size={18} /> {t.common.save}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stats;